// @flow
/* eslint-disable ta-code-conventions/has-matching-storydoc,ta-code-conventions/has-matching-test */

import * as React from 'react';
import { connect } from 'react-redux';
import type { NavigationType, RouteContext } from '@ta/lithium.context';
import { Authenticate, Debug, Device, Environment, Route, Visitor } from '@ta/lithium.context';
import { MonikerConfiguration } from '@ta/platform.monikers';
import { getMessage } from '@ta/common.i18n';
import { Messages } from '@ta/platform.i18n';
import { ToastProvider } from '@ta/core-ui.toast';
import { VirtualDOMPortalProvider } from '@ta/lithium.portals';
import PreferenceProvider from './PreferenceProvider';
import TravelInfoProvider from './TravelInfoProvider';
import LoginProvider from './LoginProvider';

type ExternalProps = {|
  children: React.Node,
|};

type Props = {|
  ...ExternalProps,
  deviceId: string,
  isWebview: boolean,
  viewportCategory: 'MOBILE' | 'TABLET' | 'DESKTOP',
  userAgentCategory: 'MOBILE' | 'TABLET' | 'DESKTOP',
  os: {| family: 'WINDOWS' | 'ANDROID' | 'IOS' | 'UNKNOWN', majorVersion: number |},
  browser: {|
    family: 'CHROME' | 'SAFARI' | 'FIREFOX' | 'EDGE' | 'IE' | 'UNKNOWN',
    majorVersion: number,
  |},
  securityToken: string,
  userId: string | null,
  locale: string,
  isRtl: boolean,
  language: string,
  cdnPrefix: string,
  serverName: string,
  version: string,
  drsInfo: string,
  domainName: string,
  lithiumRoute: RouteContext,
  servlet: string,
  sessionId: string,
|};

const LithiumBridge = ({
  children,
  deviceId,
  isWebview,
  viewportCategory,
  userAgentCategory,
  os,
  browser,
  securityToken,
  userId,
  locale,
  isRtl,
  language,
  cdnPrefix,
  serverName,
  drsInfo,
  version,
  domainName,
  lithiumRoute,
  servlet,
  sessionId,
}: Props) => {
  // We memoize each of these context creations to avoid churn on object identities
  // React re-renders components that use a context based on changes to that object as determined
  // by an Object.is check, so this is a fairly important performance optimization.
  const environmentContext = React.useMemo(
    () => ({
      locale: {
        code: locale,
        isRtl,
        language,
      },
      cdnPrefix,
      domainName,
      version,
    }),
    [locale, isRtl, cdnPrefix, language, domainName, version],
  );

  const [visitorContext, setVisitorContext] = React.useState({ userId, sessionId });

  React.useEffect(() => {
    // reload userId from redux store in non-Lithium environment
    setVisitorContext({ userId, sessionId });
  }, [userId, sessionId]);

  const deviceContext = React.useMemo(
    () => ({
      id: deviceId,
      isWebview,
      viewportCategory,
      userAgentCategory,
      os,
      browser,
      securityToken,
    }),
    [viewportCategory, deviceId, userAgentCategory, os, browser, isWebview, securityToken],
  );
  const debugContext = React.useMemo(
    () => ({
      serverName,
      drsInfo,
      ssrLogs: [],
      renderError: null,
      stagingError: null,
      debugTool: false,
      serviceCalls: [],
    }),
    [serverName, drsInfo],
  );

  const monikerConfig = React.useMemo(
    () => ({
      servlet,
    }),
    [servlet],
  );

  // Messages is a bit different; the data lives in i18n.jsx and we just provide a read-only view of it here.
  const messagesContext = {
    getMessage,
    loadMessages: () => {
      throw new Error('loadMessages is not supported in LithiumBridge');
    },
  };

  // lithiumRoute is just bootstrap data, it needs functions added to it!
  const [route, setRoute] = React.useState(lithiumRoute[0]);
  const [fragment, setFragment] = React.useState(lithiumRoute[0].fragment);
  const routeContext: any = React.useMemo(
    () => [
      {
        ...route,
        fragment,
      },
      {
        ...lithiumRoute[1],
        navigate: (newRoute, navigationType?: NavigationType = 'PAGE_VIEW') => {
          if (navigationType === 'FRAGMENT_PUSH') {
            // eslint-disable-next-line no-restricted-globals
            history.pushState(newRoute, '', newRoute.path);
            setRoute(newRoute); // to trigger a re-render
          } else {
            // eslint-disable-next-line no-restricted-globals
            window.location = route.path;
          }
        },
        navigateToFragment: newFragment => {
          // eslint-disable-next-line no-restricted-globals
          window.location.hash = newFragment || '';
          setFragment(newFragment);
        },
      },
    ],
    [lithiumRoute, route, fragment],
  );

  React.useEffect(() => {
    const handlePopState = event => {
      if (
        event.state &&
        event.state.path &&
        event.state.page &&
        event.state.params &&
        event.state.fragment !== undefined
      ) {
        setRoute(event.state);
      }
    };
    // eslint-disable-next-line no-restricted-globals
    window.addEventListener('popstate', handlePopState);
    return () => {
      // eslint-disable-next-line no-restricted-globals
      window.removeEventListener('popstate', handlePopState);
    };
  }, []);

  return (
    <Environment.Provider value={environmentContext}>
      <Debug.Provider value={debugContext}>
        <PreferenceProvider>
          <Visitor.Provider value={visitorContext}>
            <Authenticate.Provider value={setVisitorContext}>
              <Device.Provider value={deviceContext}>
                <TravelInfoProvider>
                  <Route.Provider value={routeContext}>
                    <Messages.Provider value={messagesContext}>
                      <VirtualDOMPortalProvider>
                        <LoginProvider>
                          <ToastProvider>
                            <MonikerConfiguration config={monikerConfig} suppressConfigWarnings>
                              {children}
                            </MonikerConfiguration>
                          </ToastProvider>
                        </LoginProvider>
                      </VirtualDOMPortalProvider>
                    </Messages.Provider>
                  </Route.Provider>
                </TravelInfoProvider>
              </Device.Provider>
            </Authenticate.Provider>
          </Visitor.Provider>
        </PreferenceProvider>
      </Debug.Provider>
    </Environment.Provider>
  );
};

// In the pre-lithium world, redux is still the source of truth.
export default connect<Props, ExternalProps, _, _, _, _>(state => ({
  deviceId: state.tracking.uniqueId,
  isWebview: state.meta.isNativeWebview,
  viewportCategory: state.meta.device.viewportCategory,
  userAgentCategory: state.meta.device.userAgentCategory,
  os: state.meta.device.os,
  browser: state.meta.device.browser,
  securityToken: state.meta.device.securityToken,
  userId: state.auth.loggedInUserId,
  locale: state.i18n.ietfLocale,
  isRtl: state.i18n.isRtl,
  language: state.i18n.language,
  cdnPrefix: state.meta.imageCdnUrl,
  serverName: state.tracking.serverName,
  drsInfo: state.tracking.drsInfo,
  version: `${state.tracking.vcsBranch}|${state.tracking.vcsRevision}`,
  domainName: state.meta.baseUrl.split('://')[1],
  lithiumRoute: state.lithiumRoute,
  servlet: state.meta.initialServletName,
  sessionId: state.tracking.sessionId,
}))(LithiumBridge);
